import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GitBranch, ChevronRight, Plus, User, Send, CalendarCheck, MessageSquare, StickyNote, Save, Trophy, XCircle } from "lucide-react";
import { PageBody, PageHeader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STAGE_LABEL } from "@/lib/constants";
import api from "@/lib/api";

const HEALTH = { hot: "bg-rose-500", warm: "bg-amber-500", cold: "bg-sky-500" };
const ACT_ICON = { email_sent: Send, reply: MessageSquare, meeting: CalendarCheck };

export default function Pipeline() {
  const [stages, setStages] = useState([]);
  const [opps, setOpps] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState({ value: 0, health: "warm" });
  const [noteText, setNoteText] = useState("");
  const [closeMode, setCloseMode] = useState(null);
  const [closeReason, setCloseReason] = useState("");
  const [form, setForm] = useState({ name: "", company: "", value: 24000, stage: "lead" });
  const dragging = useRef(false);
  const navigate = useNavigate();

  const load = async () => { const { data } = await api.get("/opportunities"); setStages(data.stages); setOpps(data.opportunities); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const move = async (oid, stage) => {
    setOpps((prev) => prev.map((o) => (o.id === oid ? { ...o, stage } : o)));
    try { await api.put(`/opportunities/${oid}/stage`, { stage }); toast.success(`Moved to ${STAGE_LABEL[stage]}`); }
    catch { load(); }
  };

  const createDeal = async () => {
    if (!form.company && !form.name) return toast.error("Add a name or company");
    try { await api.post("/opportunities", { ...form, value: Number(form.value) || 0 }); setCreateOpen(false); setForm({ name: "", company: "", value: 24000, stage: "lead" }); load(); toast.success("Deal added to the board"); }
    catch { toast.error("Could not create deal"); }
  };

  const openDetail = async (oid) => {
    if (dragging.current) { dragging.current = false; return; }
    try {
      const { data } = await api.get(`/opportunities/${oid}`);
      setDetail(data);
      setEdit({ value: data.opportunity.value || 0, health: data.opportunity.health || "warm" });
      setNoteText(""); setCloseMode(null); setCloseReason("");
    } catch { toast.error("Could not load deal"); }
  };

  const reloadDetail = async (oid) => { const { data } = await api.get(`/opportunities/${oid}`); setDetail(data); };

  const saveDeal = async () => {
    try {
      await api.put(`/opportunities/${detail.opportunity.id}`, { value: Number(edit.value) || 0, health: edit.health });
      await reloadDetail(detail.opportunity.id); load(); toast.success("Deal updated");
    } catch { toast.error("Could not update deal"); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await api.post(`/opportunities/${detail.opportunity.id}/notes`, { text: noteText.trim() }); setNoteText(""); await reloadDetail(detail.opportunity.id); }
    catch { toast.error("Could not add note"); }
  };

  const closeDeal = async (stage) => {
    try {
      await api.put(`/opportunities/${detail.opportunity.id}/stage`, { stage, reason: closeReason });
      toast.success(stage === "won" ? "Deal won" : "Deal marked lost");
      setDetail(null); setCloseMode(null); setCloseReason(""); load();
    } catch { toast.error("Could not close deal"); }
  };

  const fmt = (n) => (n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${n}`);
  const stageValue = (s) => opps.filter((o) => o.stage === s).reduce((a, o) => a + (o.value || 0), 0);

  return (
    <PageBody>
      <PageHeader eyebrow="Pipeline" title="Native CRM - Lead to Won"
        subtitle="Live deals for this workspace. Cards advance automatically as contacts move through the funnel."
        actions={<Button onClick={() => setCreateOpen(true)} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="pipeline-create-deal-btn"><Plus className="h-4 w-4 mr-1.5" /> Create deal</Button>}
      />
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
        {stages.map((stage) => {
          const cards = opps.filter((o) => o.stage === stage);
          return (
            <div key={stage} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragId) { move(dragId, stage); setDragId(null); } }}
              className="w-64 shrink-0" data-testid={`pipeline-column-${stage}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-sm font-medium">{STAGE_LABEL[stage]}</span>
                <span className="text-[10px] font-mono text-muted-foreground">{cards.length} · {fmt(stageValue(stage))}</span>
              </div>
              <div className="space-y-2 min-h-[120px] rounded-lg bg-card/30 border border-border/50 p-2">
                {cards.map((o) => (
                  <div key={o.id} draggable onDragStart={() => { dragging.current = true; setDragId(o.id); }} onDragEnd={() => setTimeout(() => { dragging.current = false; }, 50)}
                    onClick={() => openDetail(o.id)} data-testid="crm-deal-card"
                    className="rounded-lg border border-border/70 bg-card p-3 cursor-pointer hover:border-amber-500/50 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-medium leading-tight">{o.company || o.name}</span>
                      <span className={`h-2 w-2 rounded-full shrink-0 mt-1 ${HEALTH[o.health] || "bg-muted"}`} title={o.health} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{o.name}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="metric text-sm text-amber-500">{fmt(o.value)}</span>
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">{o.next_action}<ChevronRight className="h-3 w-3" /></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create deal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-deal-dialog">
          <DialogHeader><DialogTitle>Create deal</DialogTitle><DialogDescription>Put a card on the board manually. It stays in this workspace.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5" data-testid="deal-company" placeholder="Acme Inc" /></div>
              <div><Label>Contact name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="deal-name" placeholder="Jane Doe" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Value ($)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1.5" data-testid="deal-value" /></div>
              <div><Label>Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="deal-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={createDeal} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="save-deal-btn">Add to board</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deal detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="deal-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.opportunity.company || detail.opportunity.name}</DialogTitle>
                <DialogDescription>{STAGE_LABEL[detail.opportunity.stage]} · {detail.opportunity.value ? `$${detail.opportunity.value.toLocaleString()}` : "-"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-lg border border-border/60 p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Deal value ($)</Label><Input type="number" value={edit.value} onChange={(e) => setEdit({ ...edit, value: e.target.value })} className="mt-1.5" data-testid="detail-deal-value" /></div>
                    <div><Label className="text-xs">Health</Label>
                      <Select value={edit.health} onValueChange={(v) => setEdit({ ...edit, health: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="detail-deal-health"><SelectValue /></SelectTrigger>
                        <SelectContent>{["hot", "warm", "cold"].map((h) => <SelectItem key={h} value={h} className="capitalize">{h}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button size="sm" onClick={saveDeal} className="mt-3 rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="save-deal-edits-btn"><Save className="h-3.5 w-3.5 mr-1.5" /> Save changes</Button>
                </div>
                <div className="rounded-lg border border-border/60 p-4">
                  <div className="flex items-center gap-2 mb-2"><User className="h-4 w-4 text-amber-500" /><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Contact</span></div>
                  {detail.contact ? (
                    <div><div className="text-sm font-medium">{detail.contact.name} <span className="text-muted-foreground font-normal">· {detail.contact.title}</span></div>
                      <div className="text-xs text-muted-foreground font-mono">{detail.contact.email || "-"}</div></div>
                  ) : <div className="text-sm text-muted-foreground">{detail.opportunity.name} · {detail.opportunity.contact_email || "no contact linked"}</div>}
                </div>
                <div className="rounded-lg border border-border/60 p-4">
                  <div className="flex items-center gap-2 mb-2"><Send className="h-4 w-4 text-sky-500" /><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Campaigns</span></div>
                  {detail.campaigns.length ? detail.campaigns.map((c, i) => (
                    <div key={c.id || `camp-${i}`} className="flex items-center justify-between text-sm py-0.5"><span>{c.name}</span><span className="text-[10px] font-mono uppercase text-muted-foreground">{c.status}</span></div>
                  )) : <div className="text-sm text-muted-foreground">Not enrolled in a campaign yet.</div>}
                </div>
                <div className="rounded-lg border border-border/60 p-4">
                  <div className="flex items-center gap-2 mb-2"><StickyNote className="h-4 w-4 text-amber-500" /><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Notes</span></div>
                  <div className="flex gap-2">
                    <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNote(); }} placeholder="Log a call or context…" data-testid="deal-note-input" />
                    <Button size="sm" variant="outline" className="rounded-md shrink-0" onClick={addNote} data-testid="add-note-btn">Add</Button>
                  </div>
                  {(detail.opportunity.notes || []).length > 0 && (
                    <div className="mt-3 space-y-2" data-testid="deal-notes">
                      {[...detail.opportunity.notes].reverse().map((n) => (
                        <div key={n.id} className="text-sm border-l-2 border-amber-500/40 pl-2">
                          <div>{n.text}</div>
                          <div className="text-[10px] font-mono text-muted-foreground">{n.author} · {new Date(n.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="rounded-lg border border-border/60 p-4">
                  <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Last activity</div>
                  {detail.activity.length ? (
                    <div className="space-y-2" data-testid="deal-activity">
                      {detail.activity.map((a, i) => { const Ic = ACT_ICON[a.type] || MessageSquare; return (
                        <div key={`${a.at || a.type}-${i}`} className="flex items-start gap-2 text-sm"><Ic className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" /><div><div>{a.label}</div><div className="text-[10px] font-mono text-muted-foreground">{new Date(a.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</div></div></div>
                      ); })}
                    </div>
                  ) : <div className="text-sm text-muted-foreground">No activity logged yet.</div>}
                </div>
                {(detail.opportunity.won_reason || detail.opportunity.lost_reason) && (
                  <div className={`rounded-lg border p-4 ${detail.opportunity.stage === "won" ? "border-emerald-500/40" : "border-rose-500/40"}`} data-testid="deal-close-reason">
                    <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">{detail.opportunity.stage === "won" ? "Won reason" : "Lost reason"}</div>
                    <div className="text-sm">{detail.opportunity.won_reason || detail.opportunity.lost_reason || "-"}</div>
                  </div>
                )}
              </div>
              {closeMode ? (
                <div className="space-y-2 border-t border-border/60 pt-3">
                  <Label className="text-xs">{closeMode === "won" ? "What made this deal close?" : "Why was this deal lost?"}</Label>
                  <Textarea value={closeReason} onChange={(e) => setCloseReason(e.target.value)} rows={2} placeholder="Optional context…" data-testid="close-reason-input" />
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => { setCloseMode(null); setCloseReason(""); }}>Cancel</Button>
                    <Button size="sm" onClick={() => closeDeal(closeMode)} data-testid="confirm-close-btn"
                      className={`rounded-md text-white ${closeMode === "won" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"}`}>
                      {closeMode === "won" ? "Mark as Won" : "Mark as Lost"}
                    </Button>
                  </div>
                </div>
              ) : (
                <DialogFooter className="flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="rounded-md text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/10" onClick={() => setCloseMode("won")} data-testid="mark-won-btn"><Trophy className="h-3.5 w-3.5 mr-1.5" /> Won</Button>
                  <Button variant="outline" size="sm" className="rounded-md text-rose-600 border-rose-500/40 hover:bg-rose-500/10" onClick={() => setCloseMode("lost")} data-testid="mark-lost-btn"><XCircle className="h-3.5 w-3.5 mr-1.5" /> Lost</Button>
                  <Button variant="outline" size="sm" className="rounded-md" onClick={() => navigate(`/app/leads?focus=${encodeURIComponent(detail.opportunity.contact_email || detail.opportunity.name || "")}`)} data-testid="deal-open-contact">Open contact</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageBody>
  );
}
